/**
 * Tier 1 of the kit (PLAN §6): every board is generated at its exact finished size, never a
 * stretched template.
 *
 * Local frame: x = L (along the grain), y = W (across the grain), z = T (thickness), centred.
 * Each of the six faces is a grid whose outer cells form half of the eased arris; the grid's
 * vertices are pushed onto a rounded or chamfered box (radius r, n segments), so the arris is
 * true geometry at any board size.
 *
 * Groups: 0 = faces (both broad faces and their half of each arris), 1 = long edges, 2 = ends.
 * UV0 is in metres (plus a per-part grain offset): u runs along the grain on faces and long edges,
 * so a material's `repeat = 1 / textureScaleMetres` gives physical scale. Ply edges and ends use
 * v = 0..1 face to face (`edgeUv: 'normalised'`), because the section map is drawn per thickness.
 * Glue-ups split the faces and ends into strips with their own grain offset and tint.
 */
import * as THREE from 'three';
import { hashStr, rng } from '../textures';
import type { KitPart } from './types';

export interface BoardShape {
  /** Metres. */
  L: number;
  W: number;
  T: number;
  /** Arris radius in metres and segments per quarter arc. */
  radius: number;
  segments: number;
  kind: 'chamfer' | 'round';
  /** Strips across W (glue-up), default 1. */
  strips?: number;
  edgeUv: 'normalised' | 'metres';
  /** Grain seed: offsets the texture per part and per strip. */
  seed: number;
  /** Spread of the per-part and per-strip colour variation around 1 (vertex colour). */
  tintSpread?: number;
  /** Linear colour multiplier applied on top (a stain). */
  tint?: [number, number, number];
  /** Mitred ends: the end plane runs from the outer face at the full length to the inner face
   * `depth` metres shorter (a 45-degree mitre when depth equals the other board's thickness). */
  mitres?: Array<{ end: 1 | -1; depth: number; outer: 1 | -1 }>;
}

const EPS = 1e-9;

/** Coordinates along one axis of half-size h: arris arc points at both ends, splits in between. */
export function axisCoords(h: number, r: number, n: number, splits: number[] = []): number[] {
  if (r <= 0) return [-h, ...splits, h];
  const arc: number[] = [];
  for (let m = 0; m <= n; m++) arc.push(h - r + r * Math.tan((Math.PI / 4) * (m / n)));
  arc[n] = h;
  const inner = splits.filter((s) => s > -(h - r) + EPS && s < h - r - EPS);
  return [...arc.map((c) => -c).reverse(), ...inner, ...arc];
}

interface Offsets {
  u: number;
  v: number;
  tint: [number, number, number];
}

function makeOffsets(seed: number, count: number, spread: number): Offsets[] {
  const r = rng(seed);
  const base: [number, number, number] = (() => {
    const l = 1 + (r() - 0.5) * 2 * spread;
    const warm = (r() - 0.5) * spread;
    return [l * (1 + warm), l, l * (1 - warm)];
  })();
  return Array.from({ length: count }, () => {
    const l = 1 + (r() - 0.5) * spread * 1.4;
    return { u: r() * 7.3, v: r() * 5.1, tint: [base[0] * l, base[1] * l, base[2] * l] as [number, number, number] };
  });
}

/** Board geometry in the local frame (see module comment). */
export function createBoardGeometry(shape: BoardShape): THREE.BufferGeometry {
  const { L, W, T, kind, edgeUv } = shape;
  const h = [L / 2, W / 2, T / 2];
  const r = Math.max(0, Math.min(shape.radius, T * 0.45, W * 0.45, L * 0.45));
  const n = r > 0 ? Math.max(1, shape.segments) : 1;
  const strips = Math.max(1, Math.round(shape.strips ?? 1));
  const splits = Array.from({ length: strips - 1 }, (_, m) => -h[1] + ((m + 1) * W) / strips);
  const offs = makeOffsets(shape.seed, strips + 2, shape.tintSpread ?? 0.035);
  const edgeOffs = offs[strips];
  const endOffs = offs[strips + 1];
  const coords = [axisCoords(h[0], r, n), axisCoords(h[1], r, n, splits), axisCoords(h[2], r, n)];
  const lim = h.map((x) => Math.max(0, x - r));
  const stain = shape.tint ?? [1, 1, 1];

  const pos: number[] = [];
  const nor: number[] = [];
  const uv: number[] = [];
  const col: number[] = [];
  const idx: [number[], number[], number[]] = [[], [], []];

  const stripOf = (y: number) => Math.min(strips - 1, Math.max(0, Math.floor(((y + h[1]) / W) * strips - 1e-6)));

  function emitGrid(k: number, s: 1 | -1, ci: number[], cj: number[], stripIndex: number | null) {
    const i = (k + 1) % 3;
    const j = (k + 2) % 3;
    const group = k === 2 ? 0 : k === 1 ? 1 : 2;
    const base = pos.length / 3;
    for (const b of cj)
      for (const a of ci) {
        const p = [0, 0, 0];
        p[k] = s * h[k];
        p[i] = a;
        p[j] = b;
        const inner = p.map((c, ax) => Math.max(-lim[ax], Math.min(lim[ax], c)));
        const d = p.map((c, ax) => c - inner[ax]);
        const len = Math.hypot(d[0], d[1], d[2]);
        let q = p;
        let nn = [0, 0, 0];
        nn[k] = s;
        if (len > EPS && r > 0) {
          const dir = d.map((c) => c / len);
          const t = kind === 'round' ? r : r / (Math.abs(dir[0]) + Math.abs(dir[1]) + Math.abs(dir[2]));
          q = inner.map((c, ax) => c + dir[ax] * t);
          nn = dir;
        }
        for (const m of shape.mitres ?? []) {
          // clamp onto the mitre plane x·end = h - depth·(1 - s), s = 0 at the inner face, 1 at the outer
          const sT = (m.outer * q[2] + h[2]) / T;
          const plane = h[0] - m.depth * (1 - sT);
          if (q[0] * m.end > plane) {
            q = [plane * m.end, q[1], q[2]];
            if (k === 0) {
              const nx = m.end;
              const nz = -m.outer * (m.depth / T);
              const l = Math.hypot(nx, nz);
              nn = [nx / l, 0, nz / l];
            }
          }
        }
        pos.push(q[0], q[1], q[2]);
        nor.push(nn[0], nn[1], nn[2]);
        // UV0 (metres) and tint
        let o: Offsets;
        let u: number;
        let v: number;
        if (group === 0) {
          o = offs[stripIndex ?? stripOf(q[1])];
          u = q[0] + o.u;
          v = q[1] + o.v;
        } else if (group === 1) {
          o = { ...edgeOffs, tint: offs[q[1] < 0 ? 0 : strips - 1].tint };
          u = q[0] + o.u;
          v = edgeUv === 'normalised' ? (q[2] + h[2]) / T : q[2] + o.v;
        } else {
          const st = stripIndex ?? stripOf(q[1]);
          o = { ...endOffs, u: endOffs.u + st * 0.37, tint: offs[st].tint };
          u = q[1] + o.u;
          v = edgeUv === 'normalised' ? (q[2] + h[2]) / T : q[2] + o.v;
        }
        uv.push(u, v);
        col.push(o.tint[0] * stain[0], o.tint[1] * stain[1], o.tint[2] * stain[2]);
      }
    const ni = ci.length;
    for (let b = 0; b < cj.length - 1; b++)
      for (let a = 0; a < ni - 1; a++) {
        const v00 = base + b * ni + a;
        const v10 = v00 + 1;
        const v01 = v00 + ni;
        const v11 = v01 + 1;
        if (s > 0) idx[group].push(v00, v10, v11, v00, v11, v01);
        else idx[group].push(v00, v11, v10, v00, v01, v11);
      }
  }

  /** Split a W-axis coordinate list into one list per strip (boundaries duplicated). */
  const perStrip = (list: number[]): number[][] => {
    if (strips === 1) return [list];
    const bounds = [-Infinity, ...splits, Infinity];
    return Array.from({ length: strips }, (_, m) => list.filter((c) => c >= bounds[m] - EPS && c <= bounds[m + 1] + EPS));
  };

  for (const s of [1, -1] as const) {
    // broad faces (k = 2): i = x (L), j = y (W) -> split along j
    perStrip(coords[1]).forEach((cj, m) => emitGrid(2, s, coords[0], cj, strips > 1 ? m : null));
    // long edges (k = 1): i = z (T), j = x (L)
    emitGrid(1, s, coords[2], coords[0], null);
    // ends (k = 0): i = y (W), j = z (T) -> split along i
    perStrip(coords[1]).forEach((ci, m) => emitGrid(0, s, ci, coords[2], strips > 1 ? m : null));
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setIndex([...idx[0], ...idx[1], ...idx[2]]);
  g.addGroup(0, idx[0].length, 0);
  g.addGroup(idx[0].length, idx[1].length, 1);
  g.addGroup(idx[0].length + idx[1].length, idx[2].length, 2);
  g.computeBoundingBox();
  g.computeBoundingSphere();
  return g;
}

const AX = { x: 0, y: 1, z: 2 } as const;

export interface BoardFrame {
  /** World axis of local L, W, T. */
  perm: [number, number, number];
  /** Metres: L, W, T. */
  size: [number, number, number];
  /** Metres, placement frame (floor / left / back): box centre. */
  centre: [number, number, number];
}

/** Local board frame for a part: L along `grainWorld`, T the axis nearest the finished thickness. */
export function boardFrame(part: Pick<KitPart, 'placement' | 'grainWorld' | 'thicknessTicks'>): BoardFrame {
  const p = part.placement;
  const s = [p.sx, p.sy, p.sz];
  const iL = AX[part.grainWorld] ?? s.indexOf(Math.max(...s));
  const rest = [0, 1, 2].filter((i) => i !== iL);
  const t = part.thicknessTicks / 10;
  const iT = Math.abs(s[rest[0]] - t) <= Math.abs(s[rest[1]] - t) ? rest[0] : rest[1];
  const iW = rest.find((i) => i !== iT) as number;
  return {
    perm: [iL, iW, iT],
    size: [s[iL] / 1000, s[iW] / 1000, s[iT] / 1000],
    centre: [(p.x + p.sx / 2) / 1000, (p.y + p.sy / 2) / 1000, (p.z + p.sz / 2) / 1000],
  };
}

/** Rotate a local-frame geometry into world axes (a permutation; winding fixed for odd ones). */
export function orientGeometry(g: THREE.BufferGeometry, perm: [number, number, number]): void {
  for (const name of ['position', 'normal'] as const) {
    const a = g.getAttribute(name) as THREE.BufferAttribute;
    const arr = a.array as Float32Array;
    for (let v = 0; v < a.count; v++) {
      const l = [arr[v * 3], arr[v * 3 + 1], arr[v * 3 + 2]];
      for (let k = 0; k < 3; k++) arr[v * 3 + perm[k]] = l[k];
    }
    a.needsUpdate = true;
  }
  const odd = !((perm[0] === 0 && perm[1] === 1) || (perm[0] === 1 && perm[1] === 2) || (perm[0] === 2 && perm[1] === 0));
  if (odd) {
    const index = g.getIndex() as THREE.BufferAttribute;
    const ix = index.array as Uint32Array | Uint16Array;
    for (let t = 0; t < ix.length; t += 3) {
      const tmp = ix[t + 1];
      ix[t + 1] = ix[t + 2];
      ix[t + 2] = tmp;
    }
    index.needsUpdate = true;
  }
  g.computeBoundingBox();
  g.computeBoundingSphere();
}

export const grainSeed = (partId: string): number => hashStr(`grain:${partId}`);
